import React, { createElement } from 'react';
import cn from 'classnames';
import { Responsive } from 'apps/sigma2-ui/src/lib/hooks/useResponsive';
import { useResponsive } from 'apps/sigma2-ui/src/lib/hooks/useResponsive';

type HTMLElementType =
  | 'div'
  | 'span'
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6';

export interface ElementPublicProps {
  /**
   * If true, the text will be truncated with an ellipsis. This is useful for when you want to prevent text from overflowing.
   */
  truncate?: Responsive<boolean>;

  /**
   * If true, the class name "flex-1" will be added to the element. This is useful for when you want to make the element flex.
   */
  flex?: Responsive<boolean>;
}

export interface ElementProps extends ElementPublicProps {
  /**
   * Used for overriding the default element type. This is useful for when you want to use a different element type to match the semantics of the content.
   * @example <Element as="span">...</Element>
   */
  as?: HTMLElementType;

  /**
   * The extra class name(s) to apply to the element.
   */
  extraClassNames?: string;

  children?: React.ReactNode[] | React.ReactNode;
}

export const Element = ({
  as,
  extraClassNames,
  truncate,
  flex,
  children,
}: ElementProps): React.ReactElement | null => {
  const resolvedTruncate = useResponsive(truncate);
  const resolvedFlex = useResponsive(flex);

  const props = {
    className: cn(extraClassNames, {
      truncate: resolvedTruncate,
      'flex-1': resolvedFlex,
    }),
    children,
  };

  const elementType = as ?? 'div';

  return createElement(elementType, props);
};
